/**
 * Volume landmarks (MEV/MAV/MRV — minimum/maximum-adaptive/maximum-recoverable
 * weekly sets per muscle group) adjust set count only. They never change load
 * — the one place they touch the load decision is capping a double load jump
 * (engine.js rule R4) once the muscle group is already at its recoverable
 * ceiling, since adding load on top of MRV compounds fatigue rather than
 * driving adaptation.
 */
/**
 * Weekly working-set landmarks per `exercises.muscle_group` (an enum in
 * production: chest, back, shoulders, legs, arms, core, full_body).
 *
 * These are starting values, not measured ones — the literature's ranges are
 * wide and individual. They are deliberately conservative, and the
 * prescribed-vs-actual data the plan wants on `sets` is what will let them be
 * tuned against real adherence rather than guessed again.
 *
 * `full_body` has no entry on purpose: a full-body movement's sets belong to
 * several groups at once, so counting them against one ceiling would be wrong
 * in whichever direction you picked.
 */
export const DEFAULT_LANDMARKS = {
  chest: { mev: 10, mav: 16, mrv: 22 },
  back: { mev: 10, mav: 18, mrv: 25 },
  shoulders: { mev: 8, mav: 16, mrv: 24 },
  legs: { mev: 10, mav: 16, mrv: 22 },
  arms: { mev: 8, mav: 14, mrv: 20 },
  core: { mev: 6, mav: 12, mrv: 18 },
};

export function getLandmarks(muscleGroup) {
  return DEFAULT_LANDMARKS[muscleGroup] || null;
}

export function suppressDoubleJump(incrementSteps, weeklySetsForMuscleGroup, landmarks) {
  if (!landmarks || typeof weeklySetsForMuscleGroup !== 'number') return incrementSteps;
  const { mrv } = landmarks;
  if (typeof mrv === 'number' && weeklySetsForMuscleGroup >= mrv && incrementSteps > 1) {
    return 1;
  }
  return incrementSteps;
}

/** -1/0/+1 sets to add or cut next week for this muscle group. */
export function recommendSetDelta(weeklySetsForMuscleGroup, landmarks) {
  if (!landmarks || typeof weeklySetsForMuscleGroup !== 'number') return 0;
  const { mev, mav, mrv } = landmarks;
  if (typeof mrv === 'number' && weeklySetsForMuscleGroup >= mrv) return -1;
  if (typeof mev === 'number' && weeklySetsForMuscleGroup < mev) return 1;
  if (typeof mav === 'number' && weeklySetsForMuscleGroup < mav) return 1;
  return 0;
}
